export interface InputValidationProps {
    target: HTMLInputElement;
    type?: string | null;
    required?: boolean;
    validations?: string[];
    events?: {
        [key: string]: (ctx: _inputValidation, event?: Event) => void;
    };
}

type EventCallback = (ctx: _inputValidation, event?: Event) => void;

export class _inputValidation {
    target: HTMLInputElement;
    el: {
        message: HTMLElement | null;
    };
    type?: string | null;
    required: boolean;
    text: string | null;
    status: "error" | "success" | null;
    validations: string[];
    events: Record<string, EventCallback>;
    listeners: Record<string, EventCallback[]>;

    constructor(props: InputValidationProps) {
        this.target = props.target;

        this.el = {
            message: (this.target.parentNode?.closest(".js-validation-message") as HTMLElement | null) || null,
        };

        this.type = props.type || "text";
        this.required = props.required || false;
        this.text = null;
        this.status = null;
        this.validations = props.validations || [];
        this.events = props.events ?? {};
        this.listeners = {};

        this.init();
    }

    init(): void {
        this.target.addEventListener("input", (e) => {
            this.validate(e);
            this.emit("input", e);
        });

        this.target.addEventListener("blur", (e) => {
            this.validate(e);
            this.emit("blur", e);
        });

        this.emit("init");
    }

    validate(event: Event): void {
        this.text = null;
        this.status = null;

        const _value = event ? (event.target as HTMLInputElement).value : "";

        // if required
        if (_value.length <= 0 && this.required) {
            this.text = "required field";
            this.status = "error";
        }

        // if email
        if (event.type === "blur" && _value.length > 0 && this.validations.includes("email")) {
            if (this.isValidEmail(_value)) return;

            this.text = "Enter a valid email";
            this.status = "error";
        }

        // if phone number
        if (event.type === "input" && this.validations.includes("phone-number")) {
            (event.target as HTMLInputElement).value = _value.replace(/[^\d+]/g, "");
        }

        this.emit("beforeValidate");
        this.message();
        this.emit("afterValidate");
    }

    message(): void {
        if (!this.el.message) {
            this.el.message = document.createElement("label");
            this.el.message.classList.add(
                "js-validation-message",
                "font-sans",
                "text-e1",
                "tracking-wide",
                "uppercase",
            );
            this.target.parentNode?.insertBefore(this.el.message, this.target.nextSibling);
        }

        this.el.message.classList.remove("text-neutral-30", "text-error", "text-success");
        if (this.status === "error") this.el.message.classList.add("text-error");
        if (this.status === "success") this.el.message.classList.add("text-success");

        this.emit("beforeMessage");

        if (this.text) this.el.message.innerHTML = this.text;
        this.el.message.style.display = !this.text && !this.status ? "none" : "block";

        this.emit("afterMessage");
    }

    isValidEmail(value: string): boolean {
        const _emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return _emailRegex.test(value);
    }

    on(type: string, callback: EventCallback): void {
        if (!this.listeners[type]) this.listeners[type] = [];
        this.listeners[type].push(callback);
    }

    off(type: string, callback: EventCallback): void {
        if (!this.listeners[type]) return;
        this.listeners[type] = this.listeners[type].filter((cb) => cb !== callback);
    }

    emit(type: string, event?: Event): void {
        if (this.events[type]) this.events[type](this, event);
        if (!this.listeners[type]) return;
        this.listeners[type].forEach((callback) => callback(this, event));
    }
}
